<template>
  <el-col :span="24" id="budgets" v-loading.body="(receiving || changingPage) && !navigating" element-loading-text="Fetching page...">
      <el-row type="flex" justify="start" align="middle" id="budgets-header">
        <HeaderInfoText :colspan="9" title="BUDGETS" :meta="meta" :selection="selection"></HeaderInfoText>
        <el-col :span="6">
          <el-row type="flex" justify="center" v-if="meta.count > 20">
            <el-pagination
              layout="prev, pager, next, jumper"
              :total="meta.count"
              :page-size="20"
              @current-change="(page) => changePage({ page }, true)"
              :current-page.sync="currentPage">
            </el-pagination>
          </el-row>
        </el-col>
        <el-col :span="9" class="h-padded right-aligned-col">
          <div v-if="deleting" class="delete-notif">Deleting...</div>
          <el-select
            :disabled="!(selection && selection.length)"
            v-model="batchUpdateSelection"
            placeholder="With selected..."
            style="padding: 0 6px;"
            @change="handleBatchUpdateSelection">
            <el-option
              v-for="option in batchUpdateOptions"
              :key="option.id"
              :label="option.text"
              :value="option.id">
            </el-option>
          </el-select>
          <el-button type="primary" icon="plus" @click="createNewModel"></el-button>
        </el-col>
      </el-row>
      <el-row type="flex" justify="end" align="middle">
        <el-col :span="24">
          <el-table
            v-if="meta.count && !changingPage"
            id="budgets-table"
            :ref="`${endpoint}Table`"
            :data="data"
            border
            style="width: 100%; z-index: 0;"
            :maxHeight="tableHeight"
            @select="handleSelectionChange"
            @select-all="handleSelectionChange">
            <el-table-column type="selection" width="55" align="center"></el-table-column>
            <el-table-column prop="name" label="Budget" align="left" fit>
              <template slot-scope="scope">
                <el-row type="flex">
                  <a class="link-mock"
                     @click="editExistingModel(scope.row.id)">
                    {{ scope.row.name }}
                  </a>
                </el-row>
              </template>
            </el-table-column>
            <el-table-column prop="company" label="Company" align="left" fit>
              <template slot-scope="scope">
                <el-row type="flex">
                  {{ getCompanyById(scope.row.company).name }}
                </el-row>
              </template>
            </el-table-column>
            <el-table-column prop="production" label="Production" align="left" fit>
              <template slot-scope="scope">
                <el-row type="flex">
                  {{ getProductionById(scope.row.production).name }}
                </el-row>
              </template>
            </el-table-column>
          </el-table>
        </el-col>
      </el-row>
    <el-dialog
      :title="editMode.active ? `Edit Budget` : 'Create New Budget'"
      size="small"
      :visible.sync="dialogFormVisible"
      :close-on-press-escape="false"
      :close-on-click-modal="false"
      :show-close="false">
      <NewEntityForm
        :modalOpen="dialogFormVisible"
        :fields="fields"
        :form.sync="form"
        :idPrefix="'a-'"
        ref="form"
        @formChanged="handleFormChange">
      </NewEntityForm>
      <span slot="footer" class="dialog-footer">
        <el-button :disabled="saving || !!updating" @click="closeFormModal">Cancel</el-button>
        <el-button :loading="saving || !!updating" type="primary" @click="submitForm">Submit</el-button>
      </span>
    </el-dialog>
  </el-col>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { payload } from '@/components/RouteComponents/Budgets/actionPayload'
  import store from '@/store/store'
  import NewEntityForm from '@/components/NewEntityForm.vue'
  import HeaderInfoText from '@/components/HeaderInfoText'
  import { createNewModel } from '@/components/RouteComponents/commonFunctions'
  import { systemViewMixins } from '@/Mixins/SystemViewMixins'

  export default {
    components: {
      NewEntityForm,
      HeaderInfoText
    },
    mixins: [ systemViewMixins ],
    data () {
      return {
        tableHeight: 1000,
        batchUpdateOptions: [
          { id: 0, text: 'Delete selected' }
        ],
        batchUpdateSelection: null,
        selection: [],
        dialogFormVisible: false,
        changingPage: false,
        editMode: { active: false, modelId: null }
      }
    },
    methods: {
      // /**
      //  * Handles paginating between State pages
      //  * @param e  Target page
      //  */
      // changePage (e) {
      //   changePage({ self: this, page: e, payload, query: null, getDeps: true })
      // },
      /**
       * Resets editMode data, resets form to initial state, and displays the
       * modal to create a new model
       */
      createNewModel () {
        createNewModel(this)
      },
      handleFormChange (targetField) {
        this.trackFormChange()
        if (targetField && targetField === 'currency' && this.form[targetField] && this.form[targetField].value) {
          let promises = []
          const { value } = this.form[targetField]
          this.$store.dispatch('storeLastSearchResults', { entityType: 'currencies', mutation: 'RECEIVE_CURRENCIES' })
            .then(() => {
              let dependenciesActionPayload = {}
              const model = this.getCurrencyById(value)
              if (model && Object.keys(model).length) {
                dependenciesActionPayload.currencies = [model]
                promises.push(
                  this.$store.dispatch('findDependencies', dependenciesActionPayload)
                    .then(p => Promise.all(p))
                    .catch(e => console.log(e))
                )
              }
              return Promise.all(promises)
            })
            .catch(e => console.log(e))
        }
      }
    },
    computed: {
      ...mapGetters({
        data: 'getBudgets',
        getCompanyById: 'getCompanyById',
        getCurrencyById: 'getCurrencyById',
        getProductionById: 'getProductionById'
      })
    },
    watch: {
      budgets () {
        if (this.restoreSelection && typeof this.restoreSelection === 'function') {
          this.restoreSelection()
        }
      }
    },
    /**
     * Pre-route enter logic.
     * @param to
     * @param from
     * @param next
     */
    beforeRouteEnter (to, from, next) {
      let clearCache = !['banks', 'productions', 'companies'].includes(payload.endpoint)
      let initPromises = [store.dispatch('clearLocalState', { clearMutation: payload.clearMutation, clearCache })]
      const fieldsAlreadyExist = !typeof store.getters.getResourceFieldsByEndpoint(payload.endpoint) === 'undefined'
      if (!fieldsAlreadyExist) {
        initPromises.push(store.dispatch('fetchOptions', payload))
      }
      Promise.all(initPromises)
        .then(() => store.dispatch('fetch', payload))
        .then(entities => store.dispatch('findDependencies', entities))
        .then(promises => Promise.all(promises))
        .then(() => next())
        .catch(e => console.log(e))
    },
    created () {
      this.endpoint = payload.endpoint
      this.moduleName = payload.moduleName
      this.payload = payload
      this.generateForm()
    },
    mounted () {
      this.$nextTick = () => {
        const tableHeight = () => {
          const parentHeight = document.getElementById('budgets').offsetHeight
          const headerHeight = document.getElementById('budgets-header').offsetHeight
          return parentHeight - headerHeight
        }
        this.tableHeight = tableHeight()
      }
    },
    beforeRouteLeave (to, from, next) {
      this.$store.dispatch('isEditingForm', false)
      this.$store.dispatch('clearFilters')
        .then(() => next())
        .catch(e => {
          console.log(e)
          next()
        })
    }
  }
</script>

<style scoped>
  #budgets-header {
    border-bottom: 1px solid lightgray;
  }
  .h-padded {
    padding: 0 12px;
  }
  .right-aligned-col {
    text-align: right;
  }
  .el-table .modified-row {
    background: #c9e5f5;
  }
</style>



// WEBPACK FOOTER //
// src/components/RouteComponents/Budgets/Budgets.vue